import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useViewTracking } from '../../hooks/useViewTracking'
import { XMarkIcon, PlusIcon, TrashIcon, PencilIcon, CheckIcon } from '@heroicons/react/24/outline'
import { Button, Input } from '../../components/ui'
import {
  productsAPI, observationsAPI, technologiesAPI, vendorDocumentsAPI,
  VendorProduct, ProductImage, Observation,
} from '../../lib/services'
import FileDropzone from '../../components/ui/FileDropzone'
import VendorDocumentList from '../../components/vendor/VendorDocumentList'
import SpecSearchDropdown from '../../components/scorecard/SpecSearchDropdown'

interface VendorProductOverlayProps {
  product: VendorProduct
  vendorId: number
  onClose: () => void
  onRefresh: () => void
}

export default function VendorProductOverlay({ product: initialProduct, vendorId: _vendorId, onClose, onRefresh }: VendorProductOverlayProps) {
  const { onClose: trackedClose } = useViewTracking('vendor_product_overlay', onClose, {
    vendor_id: _vendorId, product_id: initialProduct.id, product_name: initialProduct.name,
  })
  const [product, setProduct] = useState<VendorProduct>(initialProduct)
  const [observations, setObservations] = useState<Observation[]>([])
  const [technologies, setTechnologies] = useState<{ id: number; name: string }[]>([])
  const [editingName, setEditingName] = useState(false)
  const [nameValue, setNameValue] = useState(product.name)
  const [descValue, setDescValue] = useState(product.description || '')
  const [urlValue, setUrlValue] = useState(product.product_url || '')
  const [, setSaving] = useState(false)
  const [docRefresh, setDocRefresh] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Fetch observations and specs for this product
  useEffect(() => {
    observationsAPI.list({ product: product.id, best_per_spec: true })
      .then(setObservations)
      .catch(err => console.error('Failed to load observations:', err))

    technologiesAPI.list()
      .then(data => setTechnologies(data.map((t: any) => ({ id: t.id, name: t.name }))))
      .catch(() => {})
  }, [product.id])

  const refreshProduct = async () => {
    try {
      const updated = await productsAPI.get(product.id)
      setProduct(updated)
    } catch { /* refresh is best-effort; ignore errors */ }
  }

  const saveField = async (field: string, value: any) => {
    setSaving(true)
    try {
      const updated = await productsAPI.update(product.id, { [field]: value } as any)
      setProduct(updated)
      onRefresh()
    } catch (err) {
      console.error(`Failed to save ${field}:`, err)
    } finally {
      setSaving(false)
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      await productsAPI.uploadImage(product.id, file)
      await refreshProduct()
      onRefresh()
    } catch (err) {
      console.error('Failed to upload image:', err)
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDeleteImage = async (imageId: number) => {
    try {
      await productsAPI.deleteImage(imageId)
      await refreshProduct()
      onRefresh()
    } catch (err) {
      console.error('Failed to delete image:', err)
    }
  }

  const handleTechToggle = async (techId: number) => {
    const current = product.technology_ids || []
    const updated = current.includes(techId)
      ? current.filter(id => id !== techId)
      : [...current, techId]
    await saveField('technology_ids', updated)
  }

  // Split observations by spec type
  const numericObs = observations.filter(o => o.spec_type === 'numeric' || o.spec_type === 'boolean')
  const qualitativeObs = observations.filter(o => o.spec_type === 'text')

  const overlay = (
    <div className="fixed inset-0 z-50 flex" onClick={trackedClose}>
      <div className="flex-1 bg-black/50" />
      <div
        className="w-full max-w-2xl bg-background shadow-xl overflow-y-auto border-l border-border"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {editingName ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  value={nameValue}
                  onChange={e => setNameValue(e.target.value)}
                  className="h-8 text-lg font-semibold"
                  autoFocus
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      saveField('name', nameValue)
                      setEditingName(false)
                    }
                    if (e.key === 'Escape') {
                      setNameValue(product.name)
                      setEditingName(false)
                    }
                  }}
                />
                <Button variant="ghost" size="sm" onClick={() => { saveField('name', nameValue); setEditingName(false) }}>
                  <CheckIcon className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <h2
                className="text-lg font-semibold text-foreground truncate cursor-pointer hover:text-blue-500"
                onClick={() => setEditingName(true)}
              >
                {product.name}
              </h2>
            )}
          </div>
          <button onClick={trackedClose} className="p-1 hover:bg-muted rounded ml-2">
            <XMarkIcon className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-8">
          {/* Images */}
          <section>
            <h3 className="text-sm font-medium text-foreground mb-3">Images</h3>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {(product.images || []).map((img: ProductImage) => (
                <div key={img.id} className="relative shrink-0 w-32 h-24 rounded-lg overflow-hidden border border-border group">
                  <img src={img.image_url || ''} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => handleDeleteImage(img.id)}
                    className="absolute top-1 right-1 p-0.5 bg-black/60 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <TrashIcon className="w-3.5 h-3.5 text-white" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="shrink-0 w-32 h-24 rounded-lg border border-dashed border-border flex items-center justify-center hover:border-blue-500/50 hover:bg-blue-500/5 transition-all"
              >
                <PlusIcon className="w-6 h-6 text-muted-foreground" />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </div>
          </section>

          {/* Documents */}
          <section>
            <h3 className="text-sm font-medium text-foreground mb-3">Documents</h3>
            <FileDropzone
              onFilesSelected={async (files) => {
                for (const file of files) {
                  try {
                    await vendorDocumentsAPI.upload(file, _vendorId, 'datasheet', product.id)
                  } catch (err) {
                    console.error('Failed to upload document:', err)
                  }
                }
                setDocRefresh(n => n + 1)
              }}
              label="Drop datasheets or spec sheets for this product"
            />
            <VendorDocumentList
              vendorId={_vendorId}
              productId={product.id}
              refreshTrigger={docRefresh}
              onSpecsExtracted={() => {
                refreshProduct()
                observationsAPI.list({ product: product.id, best_per_spec: true }).then(setObservations)
              }}
            />
          </section>

          {/* Description */}
          <section>
            <h3 className="text-sm font-medium text-foreground mb-2">Description</h3>
            <textarea
              value={descValue}
              onChange={e => setDescValue(e.target.value)}
              onBlur={() => { if (descValue !== (product.description || '')) saveField('description', descValue) }}
              className="w-full h-24 p-3 text-sm border border-border rounded-md bg-background text-foreground resize-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Describe this product..."
            />
          </section>

          {/* Product URL */}
          <section>
            <h3 className="text-sm font-medium text-foreground mb-2">Product URL</h3>
            <Input
              value={urlValue}
              onChange={e => setUrlValue(e.target.value)}
              onBlur={() => { if (urlValue !== (product.product_url || '')) saveField('product_url', urlValue) }}
              placeholder="https://..."
              className="text-sm"
            />
          </section>

          {/* Technologies */}
          <section>
            <h3 className="text-sm font-medium text-foreground mb-2">Technologies</h3>
            <TechSearchMultiselect
              allTechnologies={technologies}
              selectedIds={product.technology_ids || []}
              onToggle={handleTechToggle}
            />
          </section>

          {/* Technical Observations */}
          <section>
            <h3 className="text-sm font-medium text-foreground mb-3">Technical Observations</h3>
            {numericObs.length === 0 ? (
              <p className="text-xs text-muted-foreground">No technical observations yet.</p>
            ) : (
              <div className="space-y-2">
                {numericObs.map(obs => (
                  <ObservationRow key={obs.id} observation={obs} productId={product.id} onRefresh={() => {
                    observationsAPI.list({ product: product.id, best_per_spec: true }).then(setObservations)
                  }} />
                ))}
              </div>
            )}
            <AddObservationButton
              specType="numeric"
              productId={product.id}
              onCreated={() => observationsAPI.list({ product: product.id, best_per_spec: true }).then(setObservations)}
            />
          </section>

          {/* Qualitative Observations */}
          <section>
            <h3 className="text-sm font-medium text-foreground mb-3">Qualitative Observations</h3>
            {qualitativeObs.length === 0 ? (
              <p className="text-xs text-muted-foreground">No qualitative observations yet.</p>
            ) : (
              <div className="space-y-2">
                {qualitativeObs.map(obs => (
                  <ObservationRow key={obs.id} observation={obs} productId={product.id} onRefresh={() => {
                    observationsAPI.list({ product: product.id, best_per_spec: true }).then(setObservations)
                  }} />
                ))}
              </div>
            )}
            <AddObservationButton
              specType="text"
              productId={product.id}
              onCreated={() => observationsAPI.list({ product: product.id, best_per_spec: true }).then(setObservations)}
            />
          </section>
        </div>
      </div>
    </div>
  )

  return createPortal(overlay, document.body)
}

// Technology search multiselect
function TechSearchMultiselect({ allTechnologies, selectedIds, onToggle }: {
  allTechnologies: { id: number; name: string }[]
  selectedIds: number[]
  onToggle: (id: number) => void
}) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedTechs = allTechnologies.filter(t => selectedIds.includes(t.id))
  const filtered = query.length >= 1
    ? allTechnologies.filter(t =>
        t.name.toLowerCase().includes(query.toLowerCase()) && !selectedIds.includes(t.id)
      ).slice(0, 15)
    : []

  return (
    <div>
      {/* Selected chips */}
      {selectedTechs.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedTechs.map(tech => (
            <button
              key={tech.id}
              onClick={() => onToggle(tech.id)}
              className="px-2.5 py-1 text-xs rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-600 hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-500 transition-colors group flex items-center gap-1"
            >
              {tech.name}
              <XMarkIcon className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </div>
      )}

      {/* Search input */}
      <div ref={dropdownRef} className="relative">
        <input
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setIsOpen(true) }}
          onFocus={() => { if (query.length >= 1) setIsOpen(true) }}
          placeholder="Search technologies..."
          className="w-full pl-3 pr-4 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />

        {isOpen && filtered.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {filtered.map(tech => (
              <button
                key={tech.id}
                onClick={() => {
                  onToggle(tech.id)
                  setQuery('')
                  setIsOpen(false)
                }}
                className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted/50 border-b border-border/50 last:border-b-0 transition-colors"
              >
                {tech.name}
              </button>
            ))}
          </div>
        )}

        {isOpen && query.length >= 1 && filtered.length === 0 && (
          <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg px-3 py-3 text-center text-sm text-muted-foreground">
            No technologies found
          </div>
        )}
      </div>
    </div>
  )
}

// Inline observation editor row
function ObservationRow({ observation, productId, onRefresh }: { observation: Observation; productId: number; onRefresh: () => void }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(
    observation.value_text || (observation.value_numeric != null ? Math.round(observation.value_numeric).toString() : '') || (observation.value_boolean !== null ? (observation.value_boolean ? 'true' : 'false') : '')
  )

  const handleSave = async () => {
    try {
      const data: Record<string, unknown> = {
        entity_type: 'product',
        product: productId,
        spec: observation.spec,
        confidence: 5,
        observed_date: new Date().toISOString().split('T')[0],
      }
      if (observation.spec_type === 'numeric') {
        data.value_numeric = parseFloat(value)
      } else if (observation.spec_type === 'boolean') {
        data.value_boolean = value === 'true'
      } else {
        data.value_text = value
      }
      await observationsAPI.create(data)
      setEditing(false)
      onRefresh()
    } catch (err) {
      console.error('Failed to save observation:', err)
    }
  }

  const badgeColors: Record<string, string> = {
    blue: 'bg-blue-500/15 text-blue-400',
    cyan: 'bg-cyan-500/15 text-cyan-400',
    green: 'bg-green-500/15 text-green-400',
    purple: 'bg-purple-500/15 text-purple-400',
    amber: 'bg-amber-500/15 text-amber-400',
    gray: 'bg-gray-500/15 text-gray-400',
    teal: 'bg-teal-500/15 text-teal-400',
  }
  const badgeClass = badgeColors[observation.source_badge_color || 'gray'] || badgeColors.gray

  return (
    <div className="flex items-center gap-2 p-2 rounded border border-border/50 bg-muted/30">
      <span className="text-xs text-muted-foreground w-56 shrink-0 truncate" title={observation.spec_name}>{observation.spec_name || `Spec #${observation.spec}`}</span>
      {editing ? (
        <div className="flex items-center gap-1 flex-1">
          {observation.spec_type === 'text' ? (
            <textarea
              value={value}
              onChange={e => setValue(e.target.value)}
              className="flex-1 p-1 text-xs border border-border rounded bg-background text-foreground resize-none h-16"
              autoFocus
            />
          ) : (
            <Input value={value} onChange={e => setValue(e.target.value)} className="h-7 text-xs flex-1" autoFocus />
          )}
          <Button variant="ghost" size="sm" onClick={handleSave} className="h-7"><CheckIcon className="w-3.5 h-3.5" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setEditing(false)} className="h-7"><XMarkIcon className="w-3.5 h-3.5" /></Button>
        </div>
      ) : (
        <>
          <span className="text-sm text-foreground flex-1 truncate">
            {value || '—'}
            {value && observation.spec_unit && <span className="text-xs text-muted-foreground ml-1">{observation.spec_unit}</span>}
          </span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${badgeClass}`}>
            {observation.source_display_name || observation.source_type_key || 'unknown'}
          </span>
          <button onClick={() => setEditing(true)} className="p-0.5 hover:bg-muted rounded">
            <PencilIcon className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </>
      )}
    </div>
  )
}

// Button to add a new observation for a spec
function AddObservationButton({ specType, productId, onCreated }: { specType: 'numeric' | 'boolean' | 'text'; productId: number; onCreated: () => void }) {
  const [open, setOpen] = useState(false)
  const [selectedSpec, setSelectedSpec] = useState<{ id: number; name: string; spec_type: string; unit: string } | null>(null)
  const [value, setValue] = useState('')

  const handleAdd = async () => {
    if (!selectedSpec) return

    const data: Record<string, unknown> = {
      entity_type: 'product',
      product: productId,
      spec: selectedSpec.id,
      confidence: 5,
      observed_date: new Date().toISOString().split('T')[0],
    }
    if (selectedSpec.spec_type === 'numeric') {
      data.value_numeric = parseFloat(value)
    } else if (selectedSpec.spec_type === 'boolean') {
      data.value_boolean = value === 'true'
    } else {
      data.value_text = value
    }

    try {
      await observationsAPI.create(data)
      setOpen(false)
      setSelectedSpec(null)
      setValue('')
      onCreated()
    } catch (err) {
      console.error('Failed to create observation:', err)
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-2 text-xs text-blue-500 hover:text-blue-600 flex items-center gap-1"
      >
        <PlusIcon className="w-3.5 h-3.5" /> Add observation
      </button>
    )
  }

  return (
    <div className="mt-2 p-3 border border-border rounded-md bg-muted/30 space-y-2">
      {!selectedSpec ? (
        <>
          <SpecSearchDropdown
            onSelectSpec={(spec) => setSelectedSpec({ id: spec.id, name: spec.name, spec_type: spec.spec_type, unit: spec.unit })}
            category="technical"
            placeholder={`Search ${specType === 'text' ? 'qualitative' : 'technical'} specs...`}
          />
          <button
            onClick={() => { setOpen(false); setSelectedSpec(null); setValue('') }}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        </>
      ) : (
        <>
          <div className="text-xs text-foreground font-medium">{selectedSpec.name}</div>
          {selectedSpec.spec_type === 'text' ? (
            <textarea
              value={value}
              onChange={e => setValue(e.target.value)}
              placeholder="Enter value..."
              className="w-full p-2 text-xs border border-border rounded bg-background text-foreground resize-none h-16"
              autoFocus
            />
          ) : selectedSpec.spec_type === 'boolean' ? (
            <select
              value={value}
              onChange={e => setValue(e.target.value)}
              className="w-full h-8 px-2 text-xs border border-border rounded bg-background text-foreground"
            >
              <option value="">Select...</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          ) : (
            <Input
              value={value}
              onChange={e => setValue(e.target.value)}
              placeholder={`Enter numeric value${selectedSpec.unit ? ` (${selectedSpec.unit})` : ''}...`}
              type="number"
              className="h-8 text-xs"
              autoFocus
            />
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={handleAdd} className="text-xs h-7">Save</Button>
            <Button variant="ghost" size="sm" onClick={() => { setSelectedSpec(null); setValue('') }} className="text-xs h-7">Back</Button>
            <Button variant="ghost" size="sm" onClick={() => { setOpen(false); setSelectedSpec(null); setValue('') }} className="text-xs h-7">Cancel</Button>
          </div>
        </>
      )}
    </div>
  )
}
